/**
 * ============================================================
 * 经纪人管理页面 (Brokers) - v45
 * ============================================================
 * 功能：
 *   - 自动同步 settlements 表经纪人数据
 *   - 运营/招募经纪人卡片展示
 *   - 搜索框 + 类型筛选 + 绑定状态筛选
 *   - 每页16条，分页显示
 *   - 绑定注册成员/公式/模板
 * ============================================================
 */

import { useEffect, useState, useCallback } from 'react';
import { brokerApi, userApi, formulaApi, reportApi } from '../api';
import type { Broker, User, Formula, ReportTemplate } from '../types';
import {
  Users, Plus, Pencil, Trash2, X, RefreshCw, Link, Search,
  Calculator, FileText, UserCheck, ChevronLeft, ChevronRight,
  Loader
} from 'lucide-react';

const PAGE_SIZE = 16;

interface BrokerForm {
  name: string; uid: string; type: string; status: string;
  boundUserId: number; boundUserName: string;
  formulaId: number; formulaName: string;
  templateId: number; templateName: string;
  notes: string;
}

const emptyForm: BrokerForm = {
  name: '', uid: '', type: '运营经纪人', status: 'active',
  boundUserId: 0, boundUserName: '',
  formulaId: 0, formulaName: '',
  templateId: 0, templateName: '',
  notes: ''
};

export default function Brokers() {
  const [brokers, setBrokers] = useState<Broker[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [formulas, setFormulas] = useState<Formula[]>([]);
  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editingBroker, setEditingBroker] = useState<Broker | null>(null);
  const [editForm, setEditForm] = useState<BrokerForm>({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [bindFilter, setBindFilter] = useState('bind-all');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const showSuccess = useCallback((m: string) => { setToast({ message: m, type: 'success' }); setTimeout(() => setToast(null), 3000); }, []);
  const showError = useCallback((m: string) => { setToast({ message: m, type: 'error' }); setTimeout(() => setToast(null), 5000); }, []);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // 先清理重复数据
      try {
        const cleanupResult = await brokerApi.cleanupDuplicates();
        if (cleanupResult.deletedCount > 0) {
          showSuccess(`清理了 ${cleanupResult.deletedCount} 条重复的经纪人数据`);
        }
      } catch (e) {
        console.log('[Brokers] 清理重复数据时出错（可能是第一次运行）:', e);
      }

      const [bRes, uRes, fRes, tRes] = await Promise.allSettled([
        brokerApi.list(), userApi.list(), formulaApi.list(), reportApi.templates()
      ]);
      const b = bRes.status === 'fulfilled' ? bRes.value : [];
      const u = uRes.status === 'fulfilled' ? uRes.value : [];
      const f = fRes.status === 'fulfilled' ? fRes.value : [];
      const t = tRes.status === 'fulfilled' ? tRes.value : [];
      setBrokers(b); setUsers(u); setFormulas(f); setTemplates(t);
      console.log('[Brokers] 加载完成:', b.length, '人, 第一个:', b[0]);
      if (b.length === 0) { console.log('[Brokers] 列表为空，自动同步...'); await autoSync(); }
      [bRes, uRes, fRes, tRes].forEach((r, i) => { if (r.status === 'rejected') console.error(`[Brokers] API ${i} 失败:`, r.reason); });
    } catch (e: any) { showError('加载失败: ' + e.message); }
    finally { setLoading(false); }
  };

  const autoSync = async () => {
    setSyncing(true);
    try {
      const result = await brokerApi.syncFromSettlements();
      console.log(`[Brokers] 同步: 新增${result.added}, 已有${result.skipped}, 共${result.total}`);
      const updated = await brokerApi.list(); setBrokers(updated);
    } catch (err: any) { console.error('[Brokers] 同步失败:', err.message); }
    finally { setSyncing(false); }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await brokerApi.syncFromSettlements();
      showSuccess(`同步完成！新增${result.added}人，已有${result.skipped}人`);
      const updated = await brokerApi.list(); setBrokers(updated);
    } catch (err: any) { showError(err.message || '同步失败'); }
    finally { setSyncing(false); }
  };

  const openEdit = (broker: Broker | null) => {
    setEditingBroker(broker);
    if (broker) {
      setEditForm({
        name: broker.name || '', uid: broker.uid || '', type: broker.type || '运营经纪人',
        status: broker.status || 'active',
        boundUserId: (broker.bound_user_id || broker.boundUserId) || 0,
        boundUserName: (broker.bound_user_name || broker.boundUserName) || '',
        formulaId: (broker.formula_id || broker.formulaId) || 0,
        formulaName: (broker.formula_name || broker.formulaName) || '',
        templateId: (broker.template_id || broker.templateId) || 0,
        templateName: (broker.template_name || broker.templateName) || '',
        notes: ''
      });
    } else setEditForm({ ...emptyForm });
    setShowEdit(true);
  };

  const handleSave = async () => {
    if (!editForm.name.trim()) { showError('经纪人名称必填'); return; }
    setSaving(true);
    try {
      const payload: any = {
        name: editForm.name, uid: editForm.uid, type: editForm.type,
        status: editForm.status, notes: editForm.notes,
        bound_user_id: editForm.boundUserId === 0 ? null : editForm.boundUserId,
        bound_user_name: editForm.boundUserId === 0 ? null : editForm.boundUserName,
        formula_id: editForm.formulaId === 0 ? null : editForm.formulaId,
        formula_name: editForm.formulaId === 0 ? null : editForm.formulaName,
        template_id: editForm.templateId === 0 ? null : editForm.templateId,
        template_name: editForm.templateId === 0 ? null : editForm.templateName,
      };
      if (editingBroker) await brokerApi.update(editingBroker.id, payload);
      else await brokerApi.create(payload);
      showSuccess(editingBroker ? '更新成功' : '创建成功');
      setShowEdit(false); await loadData();
    } catch (err: any) { showError(err.message || '操作失败'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('确定删除？')) return;
    try { await brokerApi.delete(id); showSuccess('删除成功'); await loadData(); }
    catch { showError('删除失败'); }
  };

  // ========== 筛选逻辑 ==========
  let filtered = [...brokers];
  // 类型筛选
  if (filterType !== 'all') {
    filtered = filtered.filter(b => b.type === filterType);
  }
  // 绑定状态筛选（兼容 snake_case 和 camelCase）
  if (bindFilter !== 'bind-all') {
    filtered = filtered.filter(b => {
      const boundId = b.bound_user_id || b.boundUserId;
      const formulaId = b.formula_id || b.formulaId;
      const templateId = b.template_id || b.templateId;
      switch (bindFilter) {
        case 'bind-yes': return !!boundId;
        case 'bind-no': return !boundId;
        case 'formula-yes': return !!formulaId;
        case 'formula-no': return !formulaId;
        case 'template-yes': return !!templateId;
        case 'template-no': return !templateId;
        default: return true;
      }
    });
  }
  // 搜索
  if (search.trim()) {
    const kw = search.trim().toLowerCase();
    filtered = filtered.filter(b =>
      (b.name || '').toLowerCase().includes(kw) ||
      (b.uid || '').toLowerCase().includes(kw)
    );
  }

  // 分页
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE) || 1;
  const pageData = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // 统计数据（使用 snake_case 字段名匹配数据库返回）
  const stats = {
    total: brokers.length,
    active: brokers.filter(b => b.status === 'active').length,
    boundYes: brokers.filter(b => b.bound_user_id || b.boundUserId).length,
    boundNo: brokers.filter(b => !(b.bound_user_id || b.boundUserId)).length,
    formulaYes: brokers.filter(b => b.formula_id || b.formulaId).length,
    templateYes: brokers.filter(b => b.template_id || b.templateId).length,
    yunying: brokers.filter(b => b.type === '运营经纪人').length,
    zhaomu: brokers.filter(b => b.type === '招募经纪人').length,
  };

  const typeColors: Record<string, string> = {
    '运营经纪人': 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    '招募经纪人': 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  };

  return (
    <div className="space-y-6">
      {/* 标题栏 */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold flex items-center gap-2"><Users className="w-6 h-6 text-[#F59E0B]" />经纪人管理</h1>
        <div className="flex gap-2">
          <button onClick={handleSync} disabled={syncing} className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm transition-all disabled:opacity-50">
            {syncing ? <Loader className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}同步数据
          </button>
          <button onClick={() => openEdit(null)} className="flex items-center gap-2 px-4 py-2 bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-lg text-sm transition-all"><Plus className="w-4 h-4" />新建</button>
        </div>
      </div>

      {/* 搜索框 + 统计 */}
      <div className="flex flex-col md:flex-row gap-3 items-start md:items-center justify-between">
        <div className="relative w-full md:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
          <input
            value={search} onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
            className="w-full h-10 pl-10 pr-4 bg-[#0F172A] border border-[#334155] rounded-lg text-sm text-[#F1F5F9] placeholder-[#475569] focus:outline-none focus:border-[#3B82F6] transition-colors"
            placeholder="搜索经纪人名称或UID..."
          />
        </div>
        <div className="text-xs text-[#64748B]">共 {stats.total} 人 | 运营 {stats.yunying} | 招募 {stats.zhaomu}</div>
      </div>

      {/* 统计筛选标签 */}
      <div className="flex gap-2 flex-wrap">
        {[
          { v: 'bind-all', l: '全部成员', c: stats.total },
          { v: 'bind-yes', l: '已绑成员', c: stats.boundYes },
          { v: 'bind-no', l: '未绑成员', c: stats.boundNo },
          { v: 'formula-yes', l: '已绑公式', c: stats.formulaYes },
          { v: 'formula-no', l: '未绑公式', c: stats.total - stats.formulaYes },
          { v: 'template-yes', l: '已绑模板', c: stats.templateYes },
          { v: 'template-no', l: '未绑模板', c: stats.total - stats.templateYes },
        ].map(t => (
          <button key={t.v} onClick={() => { setBindFilter(t.v); setCurrentPage(1); }} className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all ${bindFilter === t.v ? 'bg-[#3B82F6] text-white' : 'bg-[#334155] text-[#94A3B8] hover:text-white'}`}>
            {t.l} ({t.c})
          </button>
        ))}
      </div>

      {/* 类型筛选 */}
      <div className="flex gap-2 flex-wrap">
        {[
          { v: 'all', l: '全部类型' },
          { v: '运营经纪人', l: '运营经纪人' },
          { v: '招募经纪人', l: '招募经纪人' },
        ].map(t => (
          <button key={t.v} onClick={() => { setFilterType(t.v); setCurrentPage(1); }} className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${filterType === t.v ? 'bg-emerald-600 text-white' : 'bg-[#334155] text-[#94A3B8] hover:text-white'}`}>
            {t.l}
          </button>
        ))}
      </div>

      {/* 卡片列表 */}
      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader className="w-8 h-8 text-[#3B82F6] animate-spin" /></div>
      ) : pageData.length === 0 ? (
        <div className="bg-[#1E293B] border border-[#334155] rounded-xl p-12 text-center">
          <Users className="w-12 h-12 text-[#64748B] mx-auto mb-3" />
          <p className="text-[#94A3B8] mb-4">暂无经纪人</p>
          <button onClick={handleSync} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm"><RefreshCw className="w-4 h-4 inline mr-1" />同步</button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {pageData.map(b => (
              <div key={b.id} className="bg-[#1E293B] border border-[#334155] rounded-xl p-5 hover:border-[#3B82F6]/50 transition-all group">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${b.type === '招募经纪人' ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400'}`}>
                      {b.name?.charAt(0) || '?'}
                    </div>
                    <div>
                      <h3 className="font-medium text-sm">{b.name}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${typeColors[b.type || '运营经纪人'] || 'bg-[#334155] text-[#94A3B8]'}`}>{b.type || '运营经纪人'}</span>
                    </div>
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => openEdit(b)} className="p-1.5 text-[#3B82F6] hover:bg-[#3B82F6]/10 rounded"><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => handleDelete(b.id)} className="p-1.5 text-red-400 hover:bg-red-500/10 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
                {/* 绑定信息（兼容 snake_case 和 camelCase）*/}
                <div className="space-y-2 mt-4">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-[#64748B] flex items-center gap-1"><UserCheck className="w-3 h-3" />注册成员</span>
                    {(b.bound_user_id || b.boundUserId) ? <span className="text-emerald-400 flex items-center gap-1"><Link className="w-3 h-3" />{b.bound_user_name || b.boundUserName || '已绑定'}</span> : <span className="text-[#64748B]">未绑定</span>}
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-[#64748B] flex items-center gap-1"><Calculator className="w-3 h-3" />分成公式</span>
                    {(b.formula_id || b.formulaId) ? <span className="text-amber-400 flex items-center gap-1"><Link className="w-3 h-3" />{b.formula_name || b.formulaName || '已绑定'}</span> : <span className="text-[#64748B]">未绑定</span>}
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-[#64748B] flex items-center gap-1"><FileText className="w-3 h-3" />账单模板</span>
                    {(b.template_id || b.templateId) ? <span className="text-sky-400 flex items-center gap-1"><Link className="w-3 h-3" />{b.template_name || b.templateName || '已绑定'}</span> : <span className="text-[#64748B]">未绑定</span>}
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t border-[#334155]/50 flex items-center justify-between">
                  <span className={`text-xs px-2 py-0.5 rounded ${b.status === 'active' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>{b.status === 'active' ? '启用' : '停用'}</span>
                  {b.uid && <span className="text-[10px] text-[#64748B]">UID: {b.uid}</span>}
                </div>
              </div>
            ))}
          </div>

          {/* 分页 */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 mt-6">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-2 rounded-lg bg-[#334155] hover:bg-[#475569] disabled:opacity-30 transition-all">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-[#94A3B8]">第 {currentPage} / {totalPages} 页 (共{filtered.length}条)</span>
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-2 rounded-lg bg-[#334155] hover:bg-[#475569] disabled:opacity-30 transition-all">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </>
      )}

      {/* 编辑弹窗 */}
      {showEdit && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowEdit(false)}>
          <div className="bg-[#1E293B] border border-[#334155] rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5"><h3 className="text-lg font-bold">{editingBroker ? '编辑经纪人' : '新建经纪人'}</h3><button onClick={() => setShowEdit(false)} className="text-[#64748B] hover:text-white"><X className="w-5 h-5" /></button></div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs text-[#94A3B8] mb-1.5 block">名称 *</label><input value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} className="w-full bg-[#0F172A] border border-[#334155] rounded-lg px-4 py-2.5 text-sm focus:border-[#3B82F6] focus:outline-none text-[#F1F5F9]" placeholder="经纪人名称" /></div>
                <div><label className="text-xs text-[#94A3B8] mb-1.5 block">类型</label><select value={editForm.type} onChange={e => setEditForm({ ...editForm, type: e.target.value })} className="w-full bg-[#0F172A] border border-[#334155] rounded-lg px-4 py-2.5 text-sm focus:border-[#3B82F6] focus:outline-none text-[#F1F5F9]"><option value="运营经纪人">运营经纪人</option><option value="招募经纪人">招募经纪人</option></select></div>
                <div><label className="text-xs text-[#94A3B8] mb-1.5 block">UID</label><input value={editForm.uid} onChange={e => setEditForm({ ...editForm, uid: e.target.value })} className="w-full bg-[#0F172A] border border-[#334155] rounded-lg px-4 py-2.5 text-sm focus:border-[#3B82F6] focus:outline-none text-[#F1F5F9]" placeholder="经纪人UID" /></div>
                <div><label className="text-xs text-[#94A3B8] mb-1.5 block">状态</label><select value={editForm.status} onChange={e => setEditForm({ ...editForm, status: e.target.value })} className="w-full bg-[#0F172A] border border-[#334155] rounded-lg px-4 py-2.5 text-sm focus:border-[#3B82F6] focus:outline-none text-[#F1F5F9]"><option value="active">启用</option><option value="inactive">停用</option></select></div>
              </div>
              <div className="border-t border-[#334155] pt-4">
                <h4 className="text-xs font-medium text-[#64748B] mb-3 flex items-center gap-1"><Link className="w-3 h-3" />绑定配置</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div><label className="text-xs text-[#94A3B8] mb-1.5 block">注册成员</label><select value={editForm.boundUserId} onChange={e => { const u = users.find(x => x.id === Number(e.target.value)); setEditForm({ ...editForm, boundUserId: Number(e.target.value), boundUserName: u?.name || '' }) }} className="w-full bg-[#0F172A] border border-[#334155] rounded-lg px-3 py-2.5 text-sm focus:border-[#3B82F6] focus:outline-none text-[#F1F5F9]"><option value={0}>不绑定</option>{users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.username})</option>)}</select></div>
                  <div><label className="text-xs text-[#94A3B8] mb-1.5 block">分成公式</label><select value={editForm.formulaId} onChange={e => { const f = formulas.find(x => x.id === Number(e.target.value)); setEditForm({ ...editForm, formulaId: Number(e.target.value), formulaName: f?.name || '' }) }} className="w-full bg-[#0F172A] border border-[#334155] rounded-lg px-3 py-2.5 text-sm focus:border-[#3B82F6] focus:outline-none text-[#F1F5F9]"><option value={0}>不绑定</option>{formulas.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}</select></div>
                  <div><label className="text-xs text-[#94A3B8] mb-1.5 block">账单模板</label><select value={editForm.templateId} onChange={e => { const t = templates.find(x => x.id === Number(e.target.value)); setEditForm({ ...editForm, templateId: Number(e.target.value), templateName: t?.name || '' }) }} className="w-full bg-[#0F172A] border border-[#334155] rounded-lg px-3 py-2.5 text-sm focus:border-[#3B82F6] focus:outline-none text-[#F1F5F9]"><option value={0}>不绑定</option>{templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}</select></div>
                </div>
              </div>
              <div><label className="text-xs text-[#94A3B8] mb-1.5 block">备注</label><textarea value={editForm.notes} onChange={e => setEditForm({ ...editForm, notes: e.target.value })} className="w-full bg-[#0F172A] border border-[#334155] rounded-lg px-4 py-2.5 text-sm focus:border-[#3B82F6] focus:outline-none text-[#F1F5F9] h-16 resize-none" placeholder="备注" /></div>
            </div>
            <div className="flex gap-3 pt-4 mt-4 border-t border-[#334155]">
              <button onClick={() => setShowEdit(false)} className="flex-1 py-2.5 border border-[#334155] rounded-lg text-sm hover:bg-[#334555]/30">取消</button>
              <button onClick={handleSave} disabled={saving} className="flex-1 py-2.5 bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-lg text-sm disabled:opacity-50">{saving ? '保存中...' : '保存'}</button>
            </div>
          </div>
        </div>
      )}
      {toast && <div className={`fixed bottom-6 right-6 px-4 py-3 rounded-lg text-sm font-medium shadow-lg z-50 ${toast.type === 'success' ? 'bg-emerald-500' : 'bg-red-500'} text-white`}>{toast.message}</div>}
    </div>
  );
}
